import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand, ThemeToggle, StatusBadge, ShareDialog } from "@/components/common";
import { buildTeamMap, computeWarnings } from "@/components/manage/matchUtils";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Share2, ExternalLink } from "lucide-react";

import ManageOverview from "@/components/manage/ManageOverview";
import ManageTeams from "@/components/manage/ManageTeams";
import ManageStructure from "@/components/manage/ManageStructure";
import ManageSchedule from "@/components/manage/ManageSchedule";
import ManageReferees from "@/components/manage/ManageReferees";
import ManageResults from "@/components/manage/ManageResults";
import ManageSettings from "@/components/manage/ManageSettings";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "teams", label: "Teams" },
  { id: "structure", label: "Tournament" },
  { id: "schedule", label: "Schedule" },
  { id: "referees", label: "Referees" },
  { id: "live", label: "Live" },
  { id: "results", label: "Results" },
  { id: "settings", label: "Settings" },
];

export default function TournamentManage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [share, setShare] = useState(false);

  const refresh = useCallback(async () => {
    const t = (await api.get(`/tournaments/${id}`)).data;
    const [teams, matchesRaw, referees] = await Promise.all([
      api.get(`/tournaments/${id}/teams`).then((r) => r.data),
      api.get(`/tournaments/${id}/matches`).then((r) => r.data),
      api.get(`/tournaments/${id}/referees`).then((r) => r.data),
    ]);
    const fieldMap = Object.fromEntries((t.fields || []).map((f) => [f.id, f.name]));
    const matches = matchesRaw.map((m) => ({ ...m, field: fieldMap[m.field_id] }));
    const pub = (await api.get(`/public/${t.public_code}`)).data;
    setData({ tournament: t, teams, matches, referees,
      announcements: pub.announcements, sponsors: pub.sponsors, standings: pub.standings, group_standings: pub.group_standings });
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // poll on live tab
  useEffect(() => {
    if (tab !== "live" && tab !== "overview") return;
    const iv = setInterval(refresh, 8000);
    return () => clearInterval(iv);
  }, [tab, refresh]);

  if (!data) return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;

  const { tournament: t, teams, matches, referees } = data;
  const teamMap = buildTeamMap(teams);
  const refMap = Object.fromEntries(referees.map((r) => [r.id, r.name]));
  const warnings = computeWarnings(matches, teamMap, refMap, t.match_duration || 20);
  const publicUrl = `${window.location.origin}/t/${t.public_code}`;

  const shared = { data, refresh, teamMap, refMap, warnings, setTab };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => nav("/host")} data-testid="manage-back"><ArrowLeft className="h-5 w-5" /></Button>
            <div className="min-w-0">
              <h1 className="truncate font-heading text-lg font-extrabold leading-tight">{t.name}</h1>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <StatusBadge status={t.status} /> · {teams.length}/{t.max_teams} teams · {t.paid ? "Paid" : "Unpaid"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => setShare(true)} data-testid="share-tournament"><Share2 className="mr-1 h-4 w-4" /> Share</Button>
            <Button variant="ghost" size="icon" onClick={() => window.open(publicUrl, "_blank")} data-testid="open-public"><ExternalLink className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="flex gap-1 overflow-x-auto px-3 pb-2">
          {TABS.map((tb) => (
            <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`manage-tab-${tb.id}`}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${tab === tb.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
              {tb.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        {tab === "overview" && <ManageOverview {...shared} />}
        {tab === "teams" && <ManageTeams {...shared} />}
        {tab === "structure" && <ManageStructure {...shared} />}
        {tab === "schedule" && <ManageSchedule {...shared} />}
        {tab === "referees" && <ManageReferees {...shared} />}
        {tab === "live" && <ManageResults {...shared} liveOnly />}
        {tab === "results" && <ManageResults {...shared} />}
        {tab === "settings" && <ManageSettings {...shared} />}
      </main>

      <ShareDialog open={share} onOpenChange={setShare} title="Public tournament page"
        subtitle="Anyone can scan or open this to follow the tournament." url={publicUrl} />
    </div>
  );
}
